import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus } from "lucide-react";
import { toast } from "@/lib/toast";
import { getFieldEvents, deleteFieldEvent } from "@/lib/api";
import { useConfirm } from "@/components/ConfirmDialog";
import { useCustomLists } from "@/lib/custom-lists";
import { sortByEventDate, isEventDone } from "../utils/agendaDates";
import AgendaEventCard from "./AgendaEventCard";
import AgendaTimeline from "./AgendaTimeline";

function normalizeQ(s) {
  return (s || "").toLowerCase().normalize("NFD").replace(/\p{M}/gu, "");
}

export default function FieldAgenda({ districtId, searchQuery = "" }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [confirm, ConfirmUI] = useConfirm();
  const nav = useNavigate();
  const { getList } = useCustomLists();
  const EVENT_TYPES = getList("eventTypes");

  const reload = () => {
    if (!districtId) return;
    setLoading(true);
    getFieldEvents({ district_id: districtId })
      .then(setEvents)
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line
  }, [districtId]);

  const handleDelete = async (id) => {
    const ok = await confirm({
      title: "¿Eliminar este evento del campo?",
      description: "Esta acción no se puede deshacer.",
      confirmLabel: "Eliminar",
    });
    if (!ok) return;
    try {
      await deleteFieldEvent(id);
      toast.success("Evento eliminado correctamente");
      reload();
    } catch (e) {
      console.error("Delete field event error:", e?.response?.status, e?.response?.data);
      toast.error(e?.response?.data?.detail || "No se pudo eliminar el evento");
    }
  };

  const q = normalizeQ(searchQuery);
  const sortedEvents = events
    .slice()
    .sort(sortByEventDate)
    .filter((e) => {
      if (!q) return true;
      const typeLbl = EVENT_TYPES.find((t) => t.value === e.event_type)?.label || "";
      return (
        normalizeQ(e.title).includes(q) ||
        normalizeQ(e.notes).includes(q) ||
        normalizeQ(typeLbl).includes(q)
      );
    });

  return (
    <div className="space-y-3" data-testid="field-agenda">
      {ConfirmUI}

      <Link
        to="/agenda/field/new"
        className="flex items-center justify-center gap-2 bg-brand text-white rounded-[16px] h-11 font-semibold text-[13px] shadow-[0_4px_12px_rgba(27,79,156,0.2)] transition-all duration-200 ease-out active:scale-[0.98] active:opacity-80"
        data-testid="field-event-add"
      >
        <Plus size={15} /> Nuevo evento de campo
      </Link>

      <AgendaTimeline
        items={sortedEvents}
        emptyMessage={q ? "Sin resultados" : "Sin eventos del campo"}
        emptyAction={!q && (
          <Link
            to="/agenda/field/new"
            className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-brand dark:text-blue-400 transition-all duration-150 active:opacity-70"
          >
            <Plus size={13} /> Agregar evento
          </Link>
        )}
        loading={loading}
        renderItem={(e) => (
          <AgendaEventCard
            key={e.id}
            href={`/agenda/field/${e.id}`}
            title={e.title}
            date={e.date}
            endDate={e.end_date}
            time={e.time}
            type="campo"
            typeLabel={EVENT_TYPES.find((t) => t.value === e.event_type)?.label}
            done={isEventDone(e)}
            onEdit={() => nav(`/agenda/field/${e.id}/edit`)}
            onDelete={() => handleDelete(e.id)}
            testId={`field-event-${e.id}`}
            item={e}
          />
        )}
      />
    </div>
  );
}
